import { type FC, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@ui/dialog';
import { X } from 'lucide-react';
import type { SelectedSeat } from '../store/slice';
import { formatSectorWithVisibility } from '../utils/formatSeatVisibility';
import { Button } from '@ui/button';
import { Skeleton } from '@ui/skeleton';

interface SeatViewDialogProps {
  open: boolean;
  seat: SelectedSeat | null;
  onClose: () => void;
}

export const SeatViewDialog: FC<SeatViewDialogProps> = ({ open, seat, onClose }) => {
  const [hasError, setHasError] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const imageUrl = seat?.previewImageUrl ?? null;
  const seatSector = seat ? formatSectorWithVisibility(seat.sectorName, seat.visibility) : '';

  useEffect(() => {
    setHasError(false);
    setIsImageLoading(Boolean(imageUrl));
  }, [imageUrl, seat?.seatId]);

  return (
    <Dialog open={open} onOpenChange={state => !state && onClose()}>
      <DialogContent
        hideCloseButton
        className="w-[min(98vw,1500px)] max-w-[1500px] sm:max-w-none border-none bg-transparent shadow-none px-0"
      >
        <div className="relative overflow-hidden rounded-[var(--radius-modal)] bg-surface-card shadow-[var(--shadow-2xl)]">
          <Button
            type="button"
            onClick={onClose}
            size="icon"
            className="absolute right-6 top-6 z-10 h-11 w-11 cursor-pointer rounded-full border border-background/60 bg-transparent text-text-inverse shadow-[var(--shadow-lg)]"
            aria-label="Закрыть"
          >
            <X className="h-5 w-5" />
          </Button>
          <div className="relative flex h-[70vh] w-full items-center justify-center bg-[color:var(--surface-overlay)]">
            {!hasError && imageUrl ? (
              <>
                {isImageLoading && (
                  <Skeleton className="absolute inset-0 h-full w-full rounded-none" />
                )}
                <img
                  src={imageUrl}
                  alt="Вид с места"
                  loading="lazy"
                  className={`h-full w-full object-contain transition-opacity duration-300 ${
                    isImageLoading ? 'opacity-0' : 'opacity-100'
                  }`}
                  onLoad={() => setIsImageLoading(false)}
                  onError={() => {
                    setHasError(true);
                    setIsImageLoading(false);
                  }}
                />
              </>
            ) : (
              <div className="px-8 py-24 text-center text-text-inverse">
                {imageUrl
                  ? 'Не удалось загрузить фотографию.'
                  : 'Фотография для этого места недоступна.'}
              </div>
            )}
          </div>
          <div className="px-6 pb-3 pt-3 text-left">
            <DialogTitle className="text-lg font-semibold text-text-primary">
              {seat ? `Ряд ${seat.rowName}, место ${seat.seatNumber}` : 'Вид с места'}
            </DialogTitle>
            <DialogDescription className="text-sm leading-5 text-text-secondary">
              {seatSector && (
                <span className="mb-1 block font-medium text-text-primary">{seatSector}</span>
              )}
              Приблизительный вид на арену. Реальный вид может отличаться от данной фотографии.
            </DialogDescription>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
